import { parse } from '../../util/urlHelpers';
import { storableError } from '../../util/errors';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { states as purchaseStates } from '../../transactions/transactionProcessPurchase';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //
export const FETCH_TRANSACTIONS_REQUEST = 'app/AdminTransactionsPage/FETCH_TRANSACTIONS_REQUEST';
export const FETCH_TRANSACTIONS_SUCCESS = 'app/AdminTransactionsPage/FETCH_TRANSACTIONS_SUCCESS';
export const FETCH_TRANSACTIONS_ERROR = 'app/AdminTransactionsPage/FETCH_TRANSACTIONS_ERROR';

// Default statuses shown on first load / reset
export const DEFAULT_TX_STATES = [
  purchaseStates.PURCHASED,
  purchaseStates.VALIDATED,
  purchaseStates.SHIPPED,
  purchaseStates.RECEIVED,
  purchaseStates.COMPLETED,
  purchaseStates.REVIEWED,
  purchaseStates.DISPUTED,
  purchaseStates.RESOLVED,
  purchaseStates.CANCELED,
];

export const parseStatesParam = raw => {
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  return String(raw)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

// ================ Reducer ================ //
const entityRefs = entities => entities.map(e => ({ id: e.id, type: e.type }));

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  pagination: null,
  transactionRefs: [],
  stats: null,
};

export default function adminTransactionsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TRANSACTIONS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(payload.data.data),
        pagination: payload.data.meta,
        stats: payload.data.meta?.stats || null,
      };
    case FETCH_TRANSACTIONS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //
const fetchRequest = () => ({ type: FETCH_TRANSACTIONS_REQUEST });
const fetchSuccess = response => ({ type: FETCH_TRANSACTIONS_SUCCESS, payload: response });
const fetchError = e => ({ type: FETCH_TRANSACTIONS_ERROR, error: true, payload: e });

// ================ Selectors ================ //
export const getTransactions = state =>
  getMarketplaceEntities(state, state.AdminTransactionsPage?.transactionRefs || []);

// ================ Thunks ================ //
const PAGE_SIZE = 50;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  dispatch(fetchRequest());

  const {
    page = 1,
    state: txState,
    lastTransition,
    customerId,
    providerId,
    listingId,
    dateFrom,
    dateTo,
  } = parse(search) || {};

  // No `state` in URL => apply product defaults. Explicit empty `state=` shows all.
  const statesFromUrl = parseStatesParam(txState);
  const stateFilter =
    txState === undefined || txState === null
      ? DEFAULT_TX_STATES
      : statesFromUrl && statesFromUrl.length
        ? statesFromUrl
        : null;

  // `all: true` unlocks the admin "view all transactions" mode server-side
  // (server/api/transactions-query.js). A non-admin session is ignored by the
  // server (stays scoped to self), so this is safe even if the route guard is
  // bypassed - the response would just be that user's own transactions.
  const apiQueryParams = {
    all: true,
    state: stateFilter,
    lastTransition: lastTransition || null,
    customerId: customerId || null,
    providerId: providerId || null,
    listingId: listingId || null,
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: ['listing', 'customer', 'customer.profileImage', 'provider', 'provider.profileImage'],
    'fields.transaction': [
      'processName',
      'state',
      'lastTransition',
      'lastTransitionedAt',
      'createdAt',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
    ],
    'fields.listing': ['title', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'email'],
    page,
    perPage: PAGE_SIZE,
  };

  if (dateFrom) {
    apiQueryParams.createdAtStart = new Date(dateFrom).toISOString();
  }
  if (dateTo) {
    const endDate = new Date(dateTo);
    endDate.setHours(23, 59, 59, 999);
    apiQueryParams.createdAtEnd = endDate.toISOString();
  }

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchError(storableError(e)));
      throw e;
    });
};
